import { notification } from 'antd'
import axios from '../axios'
import userPool from '../userpool'

const extractError = (err, fallback) => {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === "string") return data;
  return data.message || data.error || JSON.stringify(data);
};

export async function get(url) {
  const cognitoUser = userPool.getCurrentUser()
  function getCurrentUser() {
    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err)
        }
        axios
          .get(url, {
            headers: {
              Authorization: `${session.idToken.jwtToken}`,
            },
          })
          .then((response) => {
            resolve(response.data)
          })
          .catch(() => {
            resolve(false)
          })
      })
    })
  }
  if (cognitoUser != null) return getCurrentUser()

  return false
}

export async function post(url, payload, successMessage = 'Success', errorMessage = 'Error') {
  const cognitoUser = userPool.getCurrentUser()
  return new Promise((resolve, reject) => {
    cognitoUser.getSession((err, session) => {
      if (err) {
        reject(err)
      }
      axios
        .post(
          url,
          { ...payload },
          {
            headers: {
              Authorization: session.idToken.jwtToken,
            },
          },
        )
        .then(() => {
          notification.success({ message: successMessage })
          resolve(true)
        })
        .catch((err) => {

          notification.error({ message: err?.response?.data || errorMessage })
          resolve(false)
        })
    })
  })
}

export async function postReturnData(url, payload) {
  const cognitoUser = userPool.getCurrentUser()
  return new Promise((resolve, reject) => {
    cognitoUser.getSession((err, session) => {
      if (err) {
        reject(err)
      }
      axios
        .post(
          url,
          { ...payload },
          {
            headers: {
              Authorization: session.idToken.jwtToken,
            },
          },
        )
        .then((response) => {
          resolve(response.data)
        })
        .catch(() => {
          resolve(false)
        })
    })
  })
}

export async function getCompanyCodes() {
  return get('/admin/companyCodes')
}

export async function generateUploadUrl(payload) {
  return postReturnData('/admin/generateUploadUrl', payload)
}

export async function getAdminViaData(url, data) {
  const cognitoUser = userPool.getCurrentUser()
  function getCurrentUser() {
    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err)
        }
        axios
          .get(url, {
            headers: {
              Authorization: `${session.idToken.jwtToken}`,
            },
            params: data.payload, // Send data as query parameters
          })
          .then((response) => {
            let data = response.data;
            console.log('Raw response.data:', data);

            if (data) {
              if (typeof data.body === 'string') {
                try {
                  const parsed = JSON.parse(data.body);
                  console.log('Parsed body:', parsed);
                  resolve(parsed);
                  return;
                } catch (e) {
                  console.error('Failed to parse response body:', e, data.body);
                }
              } else if (Array.isArray(data)) {
                console.log('Response is already an array');
                resolve(data);
                return;
              } else if (data.body && Array.isArray(data.body)) {
                console.log('Response.body is an array');
                resolve(data.body);
                return;
              }
            }

            console.log('Admin VIA Data (final):', data);
            resolve(data);
          })
          .catch((err) => {
            if (err?.response?.data) {
              let errorData = err.response.data;

              if (errorData && typeof errorData.body === 'string') {
                try {
                  errorData = JSON.parse(errorData.body);
                } catch (e) {
                  console.error('Failed to parse error response body:', e);
                }
              }

              resolve(errorData)
            } else {
              resolve(false)
            }
          })
      })
    })
  }
  if (cognitoUser != null) return getCurrentUser()

  return false
}

// ---------------------------------------------------------------------------
// Cleanup dashboard (read-only data-hygiene tools). Every endpoint is a POST that
// only ever READS - it scans / browses DynamoDB, S3 and Aurora and reports what is
// stale / orphaned / reclaimable. Nothing here deletes. The API Gateway wraps the
// body as { context, body }; server-side `cleanup_params` unwraps it, so from the
// client we just POST the params object straight through.
// ---------------------------------------------------------------------------
export async function postCleanup(url, payload = {}) {
  const cognitoUser = userPool.getCurrentUser()
  function getCurrentUser() {
    return new Promise((resolve) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          notification.error({ message: 'Session expired. Please login again.' })
          resolve(false)
          return
        }
        axios
          .post(url, payload, {
            headers: {
              Authorization: `${session.idToken.jwtToken}`,
            },
          })
          .then((response) => {
            resolve(response.data)
          })
          .catch((error) => {
            const message = extractError(error, 'Cleanup request failed')
            notification.error({ message })
            resolve(false)
          })
      })
    })
  }
  if (cognitoUser != null) return getCurrentUser()

  notification.error({ message: 'Not logged in. Please login first.' })
  return false
}

// Scanners (analysis: "what is stale / orphaned / reclaimable")
export async function cleanupScanDynamo(payload) {
  return postCleanup('/admin/cleanup/scan/dynamo', payload)
}

export async function cleanupScanS3(payload) {
  return postCleanup('/admin/cleanup/scan/s3', payload)
}

export async function cleanupScanRds(payload) {
  return postCleanup('/admin/cleanup/scan/rds', payload)
}

// Explorers (lazy navigation: drill through the data before deciding anything)
export async function cleanupExploreDynamo(payload) {
  return postCleanup('/admin/cleanup/explore/dynamo', payload)
}

export async function cleanupExploreS3(payload) {
  return postCleanup('/admin/cleanup/explore/s3', payload)
}

export async function cleanupExploreRds(payload) {
  return postCleanup('/admin/cleanup/explore/rds', payload)
}

// Entity search ("show me everywhere this ID lives") - read-only, returns matches
// already shaped as candidates/add items.
export async function cleanupEntitySearch(payload) {
  return postCleanup('/admin/cleanup/entity/search', payload)
}

// Delete pipeline. candidates/add is the only write-path into the queue; approve
// starts backup + quarantine (nothing is deleted immediately); restore undoes a
// quarantined or deleted candidate. The hard delete only ever happens in the
// scheduled sweep after the quarantine window passes.
export async function cleanupCandidatesAdd(payload) {
  return postCleanup('/admin/cleanup/candidates/add', payload)
}

export async function cleanupCandidatesGet(payload) {
  return postCleanup('/admin/cleanup/candidates', payload)
}

export async function cleanupApprove(payload) {
  return postCleanup('/admin/cleanup/approve', payload)
}

export async function cleanupRestore(payload) {
  return postCleanup('/admin/cleanup/restore', payload)
}

// GET /admin/aura/missions with query params
export async function getViaMissions(params) {
  const cognitoUser = userPool.getCurrentUser()
  function getCurrentUser() {
    return new Promise((resolve) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          notification.error({ message: 'Session expired. Please login again.' })
          resolve(false)
          return
        }
        axios
          .get('/admin/aura/missions', {
            headers: {
              Authorization: `${session.idToken.jwtToken}`,
            },
            params,
          })
          .then((response) => {
            resolve(response.data)
          })
          .catch((err) => {
            const message = extractError(err, 'Error fetching missions')
            notification.error({ message })
            resolve(false)
          })
      })
    })
  }
  if (cognitoUser != null) return getCurrentUser()

  return false
}

// POST /admin/aura/missions to create a mission
export async function createViaMission(payload) {
  const cognitoUser = userPool.getCurrentUser()
  function getCurrentUser() {
    return new Promise((resolve) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          notification.error({ message: 'Session expired. Please login again.' })
          resolve(false)
          return
        }
        axios
          .post('/admin/aura/missions', payload, {
            headers: {
              Authorization: `${session.idToken.jwtToken}`,
            },
          })
          .then((response) => {
            resolve(response.data)
          })
          .catch((err) => {
            const messageText = extractError(err, 'Error creating mission')
            notification.error({ message: messageText })
            resolve(false)
          })
      })
    })
  }
  if (cognitoUser != null) return getCurrentUser()

  notification.error({ message: 'Not logged in. Please login first.' })
  return false
}

// PUT /admin/aura/missions to update a mission
export async function updateViaMission(payload) {
  const cognitoUser = userPool.getCurrentUser()
  function getCurrentUser() {
    return new Promise((resolve) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          notification.error({ message: 'Session expired. Please login again.' })
          resolve(false)
          return
        }
        axios
          .put('/admin/aura/missions', payload, {
            headers: {
              Authorization: `${session.idToken.jwtToken}`,
            },
          })
          .then((response) => {
            resolve(response.data)
          })
          .catch((err) => {
            const messageText = extractError(err, 'Error updating mission')
            notification.error({ message: messageText })
            resolve(false)
          })
      })
    })
  }
  if (cognitoUser != null) return getCurrentUser()

  notification.error({ message: 'Not logged in. Please login first.' })
  return false
}

// DELETE /admin/aura/missions to delete a mission
export async function deleteViaMission(payload) {
  const cognitoUser = userPool.getCurrentUser()
  function getCurrentUser() {
    return new Promise((resolve) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          notification.error({ message: 'Session expired. Please login again.' })
          resolve(false)
          return
        }
        axios
          .delete('/admin/aura/missions', {
            headers: {
              Authorization: `${session.idToken.jwtToken}`,
            },
            data: payload,
          })
          .then((response) => {
            resolve(response.data)
          })
          .catch((err) => {
            const messageText = extractError(err, 'Error deleting mission')
            notification.error({ message: messageText })
            resolve(false)
          })
      })
    })
  }
  if (cognitoUser != null) return getCurrentUser()

  notification.error({ message: 'Not logged in. Please login first.' })
  return false
}

// GET /admin/aura/studies with query params mapped to client-side sorting/filtering
export async function getViaStudies(params) {
  const cognitoUser = userPool.getCurrentUser()
  function getCurrentUser() {
    return new Promise((resolve) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          notification.error({ message: 'Session expired. Please login again.' })
          resolve(false)
          return
        }

        const sortMap = {
          'study_id': 'studyId',
          'studyId': 'studyId',
          'int_ref': 'companyCode',
          'clientCode': 'companyCode',
          'complete': 'complete',
          'isActive': 'isActive',
          'isRecruitmentOpen': 'isRecruitmentOpen',
          'recruitment_done': 'recruitmentDone',
          'start_date': 'startDate',
          'startDate': 'startDate',
          'end_date': 'endDate',
          'endDate': 'endDate',
        }
        const mappedSortBy = sortMap[params.sortBy] || params.sortBy || 'studyId'

        // Map UI params to API expected params
        const apiParams = {
          page: params.page || 1,
          pageSize: params.pageSize || 20,
          sortBy: mappedSortBy,
          sortDir: params.sortDir || 'asc',
          q: params.q || undefined,
        }

        if (params.studyId || params.study_id) apiParams.studyId = params.studyId || params.study_id

        // Pass both new and old filter parameters to ensure backend matches either
        if (params.isActive !== undefined) {
          apiParams.isActive = params.isActive
          apiParams.complete = !params.isActive
        } else if (params.complete !== undefined) {
          apiParams.complete = params.complete === 1
          apiParams.isActive = params.complete !== 1
        }

        if (params.isRecruitmentOpen !== undefined) {
          apiParams.isRecruitmentOpen = params.isRecruitmentOpen
          apiParams.recruitmentDone = !params.isRecruitmentOpen
        } else if (params.recruitment_done !== undefined) {
          apiParams.recruitmentDone = params.recruitment_done === 1
          apiParams.isRecruitmentOpen = params.recruitment_done !== 1
        } else if (params.recruitmentDone !== undefined) {
          apiParams.recruitmentDone = params.recruitmentDone === 1
          apiParams.isRecruitmentOpen = params.recruitmentDone !== 1
        }

        axios
          .get('/admin/aura/studies', {
            headers: {
              Authorization: `${session.idToken.jwtToken}`,
            },
            params: apiParams,
          })
          .then((response) => {
            const data = response.data || {}
            const rawItems = data.items || []

            // Map backend DynamoDB fields to UI expected structure
            let items = rawItems.map((item) => {
              const studyInfo = item.studyInfo || {}
              return {
                study_id: item.studyId || '',
                int_ref: studyInfo.companyCode || studyInfo.intRef || '',
                complete: item.complete ? 1 : 0,
                recruitment_done: item.recruitmentDone ? 1 : 0,
                start_date: studyInfo.startDate || '',
                end_date: studyInfo.endDate || '',

                // Map new fields
                description: studyInfo.description || '',
                joinByDate: studyInfo.joinByDate || '',
                logo: studyInfo.logo || '',
                orgName: studyInfo.orgName || '',
                contactEmail: studyInfo.contactEmail || '',
                reward: studyInfo.reward || '',
                studyInstructions: studyInfo.studyInstructions || '',
                studyTermsTitle: studyInfo.studyTermsTitle || '',
                studyTerms: studyInfo.studyTerms || '',
                studyName: studyInfo.studyName || '',
                termsLink: studyInfo.termsLink || '',
                useOfData: studyInfo.useOfData || '',
                defaultStudyLocale: studyInfo.defaultStudyLocale || '',
                termsTitle: studyInfo.termsTitle || '',

                emailReminders: studyInfo.emailReminders === true,
                missionReminders: studyInfo.missionReminders === true,
                st: studyInfo.st === true,
                studyInfoExpanded: studyInfo.studyInfoExpanded === true,

                missionReminders_root: item.missionReminders === true,
                welcomEmailReminder: item.welcomEmailReminder === true,

                endOfStudyConditions: studyInfo.endOfStudyConditions,
                userRequiredDevices: studyInfo.userRequiredDevices,
                dataConnections: studyInfo.dataConnections,
                redirects: studyInfo.redirects,
                licenses: item.licenses,
                ...item,
                raw: item,
              }
            })

            resolve({
              items: items,
              total: data.total || items.length,
            })
          })
          .catch((err) => {
            const message = extractError(err, 'Error fetching studies')
            notification.error({ message })
            resolve(false)
          })
      })
    })
  }
  if (cognitoUser != null) return getCurrentUser()

  return false
}

// POST /admin/aura/studies
export async function createViaStudy(payload) {
  const cognitoUser = userPool.getCurrentUser()
  function getCurrentUser() {
    return new Promise((resolve) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          notification.error({ message: 'Session expired. Please login again.' })
          resolve(false)
          return
        }

        axios
          .post('/admin/aura/studies', payload, {
            headers: {
              Authorization: `${session.idToken.jwtToken}`,
            },
          })
          .then((response) => {
            resolve(response.data)
          })
          .catch((err) => {
            const messageText = extractError(err, 'Error creating study')
            notification.error({ message: messageText })
            resolve(false)
          })
      })
    })
  }
  if (cognitoUser != null) return getCurrentUser()

  notification.error({ message: 'Not logged in. Please login first.' })
  return false
}

// PUT /admin/aura/studies to update a study
export async function updateViaStudy(payload) {
  const cognitoUser = userPool.getCurrentUser()
  function getCurrentUser() {
    return new Promise((resolve) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          notification.error({ message: 'Session expired. Please login again.' })
          resolve(false)
          return
        }

        axios
          .put('/admin/aura/studies', payload, {
            headers: {
              Authorization: `${session.idToken.jwtToken}`,
            },
          })
          .then((response) => {
            resolve(response.data)
          })
          .catch((error) => {
            notification.error({
              message: 'Error updating study',
              description: extractError(error, error.message),
            })
            resolve(false)
          })
      })
    })
  }
  if (cognitoUser != null) return getCurrentUser()

  notification.error({ message: 'Not logged in. Please login first.' })
  return false
}

// DELETE /admin/aura/studies
export async function deleteViaStudy(payload) {
  const cognitoUser = userPool.getCurrentUser()
  function getCurrentUser() {
    return new Promise((resolve) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          notification.error({ message: 'Session expired. Please login again.' })
          resolve(false)
          return
        }

        const apiPayload = {
          studyId: payload.study_id
        }

        axios
          .delete('/admin/aura/studies', {
            headers: {
              Authorization: `${session.idToken.jwtToken}`,
            },
            params: apiPayload,
          })
          .then((response) => {
            resolve(response.data)
          })
          .catch((err) => {
            const messageText = extractError(err, 'Error deleting study')
            notification.error({ message: messageText })
            resolve(false)
          })
      })
    })
  }
  if (cognitoUser != null) return getCurrentUser()

  notification.error({ message: 'Not logged in. Please login first.' })
  return false
}